import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';

interface GoogleCalendarCallbackData {
  googleCalendarCallback: {
    success: boolean;
    message: string;
  };
}

const GOOGLE_CALENDAR_CALLBACK = gql`
  mutation GoogleCalendarCallback($code: String!, $state: String!) {
    googleCalendarCallback(code: $code, state: $state) {
      success
      message
    }
  }
`;

export function GoogleCalendarCallback() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Connecting your Google Calendar...');

  const [callbackMutation] = useMutation<GoogleCalendarCallbackData>(GOOGLE_CALENDAR_CALLBACK);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      setStatus('error');
      setMessage(`Authorization failed: ${error}`);
      return;
    }

    if (!code || !state) {
      setStatus('error');
      setMessage('Missing authorization code or state. Please try again.');
      return;
    }

    // Call the callback mutation
    callbackMutation({
      variables: { code, state },
    })
      .then((result) => {
        setStatus('success');
        setMessage(result.data?.googleCalendarCallback?.message || 'Google Calendar connected successfully!');
      })
      .catch((err) => {
        setStatus('error');
        setMessage(`Failed to connect: ${err.message}`);
      });
  }, [searchParams, callbackMutation]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        {status === 'processing' && (
          <>
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
            </div>
            <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
              Connecting...
            </h1>
            <p className="text-center text-gray-600">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center mb-4">
              <svg
                className="w-16 h-16 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
              Success!
            </h1>
            <p className="text-center text-gray-600 mb-6">{message}</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 text-center">
                You can now close this window and return to the app.
              </p>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center mb-4">
              <svg
                className="w-16 h-16 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
              Connection Failed
            </h1>
            <p className="text-center text-gray-600 mb-6">{message}</p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 text-center">
                Please close this window and try again from the app.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
